import { ErrorHandler, Injectable, Injector, inject } from '@angular/core';
import { take } from 'rxjs/operators';
import { appSettings } from '@nha/config';
import { ClientExceptionService } from '../services/client-exception.service';

const WINDOW_MS = 60_000;

// Global, not per-signature.
const MAX_POSTS_PER_WINDOW = 20;

/** Anything can be thrown, so read the fields we key on defensively rather than assuming an Error. */
type ErrorLike = { message?: unknown; url?: unknown; status?: unknown };

@Injectable()
export class GlobalErrorHandler extends ErrorHandler {
	private injector = inject(Injector);

	private lastPostedAt = new Map<string, number>();

	handleError(error: unknown): void {
		// Reporting must never throw: an escaping error re-enters handleError, which is the loop this
		// whole class guards against. postToSeq reads error.message and error.stack unguarded.
		try {
			if (appSettings.logger.logErrors && this.shouldPost(error)) {
				const clientExceptionService: ClientExceptionService = this.injector.get(ClientExceptionService);

				// Without an error callback RxJS rethrows asynchronously, and the rethrow lands back here.
				clientExceptionService
					.postToSeq(error as Error)
					.pipe(take(1))
					.subscribe({ error: () => undefined });
			}
		} catch {
			// A failure to report is not worth reporting.
		} finally {
			/** hand the error off to the browser */
			super.handleError(error);
		}
	}

	/**
	 * One post per signature per window, and at most MAX_POSTS_PER_WINDOW overall. Since both limits
	 * share a window, the live entry count is the number of posts made in it.
	 */
	private shouldPost(error: unknown): boolean {
		const now = Date.now();

		for (const [signature, postedAt] of this.lastPostedAt) {
			if (now - postedAt >= WINDOW_MS) {
				this.lastPostedAt.delete(signature);
			}
		}

		const signature = this.getSignature(error);
		if (this.lastPostedAt.has(signature) || this.lastPostedAt.size >= MAX_POSTS_PER_WINDOW) {
			return false;
		}

		this.lastPostedAt.set(signature, now);

		return true;
	}

	// Errors repeat by value, not by object identity.
	private getSignature(error: unknown): string {
		const { status, url, message } = (error ?? {}) as ErrorLike;

		return `${status ?? ''}|${url ?? ''}|${message ?? error ?? ''}`;
	}
}
